import { Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AlertButtonChip from '@components/AlertButtonChip';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import DataTable from '@components/DataTable';
import { RecordSourcePill } from '@components/RecordSourcePill';

import { DATATABLE_COLUMNS } from '@constants/columns';
import { getObservationConfig } from '@constants/fhir';

import { useHealthChart } from '@hooks/useHealthChart';
import { useSession } from '@hooks/useSession';

import type { DataPointMapped } from '@models/data-point';
import { Interval } from '@models/graph';
import { HEALTH_TRACKER_MAP } from '@models/vital-sign';

import { hasTimeComponent } from '@utils/date';
import { capitalize, formatDate, formatVitalSign } from '@utils/format';

import type {
  CurationStatus,
  ObservationAlertModel,
  ResourceSourceType,
} from 'generated/types';

const messages = defineMessages({
  curationStatus: {
    id: 'GLOBAL.curationStatus',
    defaultMessage: 'Curation Status',
  },
  recordSource: {
    id: 'GLOBAL.recordSource',
    defaultMessage: 'Record Source',
  },
  value: {
    id: 'GLOBAL.value',
    defaultMessage: 'Value',
  },
  date: {
    id: 'GLOBAL.date',
    defaultMessage: 'Date',
  },
  source: {
    id: 'GLOBAL.source',
    defaultMessage: 'Source',
  },
  clinical: {
    id: 'vitalSigns.clinical',
    defaultMessage: 'Clinical',
  },
  nonClinical: {
    id: 'vitalSigns.nonClinical',
    defaultMessage: 'Non-Clinical',
  },
  noData: {
    id: 'healthTracker.detail.table.noData',
    defaultMessage: 'metrics between {minDate} and {maxDate}.',
  },
  alert: {
    id: 'vitalSigns.alert',
    defaultMessage: 'Alert',
  },
});

export default function DrilldownTable() {
  const {
    state: { isVitall, isPractitioner },
  } = useSession();
  const { formatMessage } = useIntl();
  const { category, code, data, interval, maxDate, minDate, showAlerts } =
    useHealthChart();
  const { backRoute } = HEALTH_TRACKER_MAP[category];

  const config = getObservationConfig(code);
  const keys = (config?.keys ?? [])
    .map((o) => Object.values(o))
    .flat()
    .reduce<string[]>((acc, curr) => {
      if (!acc.includes(curr)) {
        acc.push(curr);
      }
      return acc;
    }, []);
  const format = config?.format;

  const tableData = dataToTable(data);
  const dateDisplay =
    config?.graph?.showTime || interval === Interval.Hour ? 'datetime' : 'date';

  const columns = (
    code in DATATABLE_COLUMNS
      ? DATATABLE_COLUMNS[code]
      : DATATABLE_COLUMNS.default
  ).concat([
    {
      name: 'curationStatus',
      title: formatMessage(messages.curationStatus),
      render: (status: CurationStatus) => (
        <CurationStatusBadge status={status} />
      ),
      show: Boolean(isVitall),
    },
    {
      name: 'recordSource',
      title: formatMessage(messages.recordSource),
      render: (type: ResourceSourceType) => <RecordSourcePill type={type} />,
      show: Boolean(isVitall || isPractitioner),
    },
    {
      name: 'alertDate',
      title: formatMessage(messages.alert),
      show: showAlerts,
      render: (alert?: ObservationAlertModel) =>
        alert ? (
          <Typography
            color="text.secondary"
            key={`alert-date-${alert.alert_id}`}
            variant="body2"
          >
            {formatDate({ date: alert.created_at, formatter: 'datetime' })}
          </Typography>
        ) : null,
    },
    {
      name: 'alert',
      show: showAlerts,
      render: (alert?: ObservationAlertModel) => {
        const codes = alert?.codes ?? [];
        // ['systolic', 'diastolic'] -> 'Systolic & Diastolic'
        const prefix = codes.reduce((acc, curr, index) => {
          acc = acc.concat(
            `${capitalize(curr)}${index !== codes.length - 1 ? ' & ' : ''}`
          );
          return acc;
        }, '');
        return (
          <>
            {alert ? (
              <AlertButtonChip
                alert_id={alert.alert_id}
                key={`observation-alert-${alert.alert_id}`}
                prefix={prefix}
                status={alert.status}
              />
            ) : null}
          </>
        );
      },
    },
  ]);

  return (
    <DataTable
      // border={{
      //   boxShadow: shadows[10],
      //   showBorder: true,
      //   tableBackgroundColor: grey.A100,
      // }}
      columns={columns}
      data={tableData.map((record) => {
        return {
          ...record.common,
          value: `${formatVitalSign(
            keys
              .map((key) => record[key] ?? '')
              .filter((r) => Boolean(r) || typeof r === 'number'),
            undefined,
            format ? (value) => format(value, record.common.unit) : undefined
          )} ${record.common.unit ?? ''}`,
          date: formatDate({
            date: record.common.date,
            formatter:
              dateDisplay === 'datetime' &&
              !hasTimeComponent(record.common.record?.testDate)
                ? 'date'
                : dateDisplay,
          }),
        };
      })}
      noHeader
      onClick={(record: Record<string, any>) => {
        if (record.curationStatus && record.id) {
          navigate(`${backRoute}${code}/${record.id}`);
        }
      }}
      showHead
      sortBy="date"
      type={formatMessage(messages.noData, {
        minDate: formatDate({
          date: minDate,
          formatter: dateDisplay,
        }),
        maxDate: formatDate({
          date: maxDate,
          formatter: dateDisplay,
        }),
      })}
    />
  );
}

function dataToTable(data: DataPointMapped[]) {
  const dedupedData = data.reduce<Record<string, Record<string, any>>>(
    (acc, point) => {
      const { id } = point.record;
      if (id) {
        if (id in acc) {
          acc[id][point.type] = point.value;
          acc[id].common = { ...point, time: point.date };
        } else {
          acc[id] = {
            [point.type]: point.value,
            common: { ...point, time: point.date },
          };
        }
      }
      return acc;
    },
    {}
  );

  return Object.values(dedupedData);
}
